// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

// Layouts and Pages
import AdminLayout from './components/AdminLayout';
import DashboardPage from './pages/DashboardPage';
import SetupProgramPage from './pages/SetupProgramPage';
import ConfirmEmail from './components/auth/ConfirmEmail';
import AuthCallbackPage from './pages/AuthCallbackPage';
import { ProgramProvider } from './context/ProgramContext'; // Context Provider
import SelectProgramPage from './pages/SelectProgramPage'; // Program Selection Page
import ManageProgramPage from './pages/ManageProgramPage'; // Program Management Page
import ParticipantsPage from './pages/ParticipantsPage'; // Participants Page
import ScanQRPage from './pages/ScanQRPage'; // Scan QR Page
import SubscriptionPage from './pages/SubscriptionPage';
import RewardsPage from './pages/RewardsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ProfilePage from './pages/ProfilePage';
import HelpPage from './pages/HelpPage';

// API Client
import apiClient from './api'; // Your configured axios instance

// Styles
import './App.css';


function App() {
  // --- State Definitions ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true); // State to track initial auth check

  // --- Check for existing token on initial app load ---
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      console.log("App.js useEffect: Checking initial auth status...");
      if (token) {
        console.log("App.js useEffect: Token found in localStorage.");
        // Verify token with the backend
        try {
          // Use the apiClient - the interceptor will add the token header
          await apiClient.get('/users/me'); // Simple request to a protected route
          console.log("App.js useEffect: Token verified with /users/me.");
          setIsAuthenticated(true); // Set authenticated state if token is valid
        } catch (error) {
          // If /users/me fails (e.g., 401), the token is invalid/expired
          console.warn("App.js useEffect: Token verification failed.", error.response?.data || error.message);
          localStorage.removeItem('authToken'); // Remove invalid token
          setIsAuthenticated(false);
        }
      } else {
        console.log("App.js useEffect: No token found.");
        setIsAuthenticated(false);
      }
      setIsLoadingAuth(false); // Finished checking auth status
    };

    checkAuthStatus();
  }, []); // Empty dependency array ensures this runs only once on mount

  // --- Modal Handlers (useCallback for stability) ---
  const openModal = useCallback((content) => {
    console.log("App.js: Calling openModal with:", content);
    setModalContent(content);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    console.log("App.js: Calling closeModal");
    setIsModalOpen(false);
    setModalContent(null);
  }, []);

  // --- Authentication Handlers (useCallback for stability) ---
  const handleLoginSuccess = useCallback(() => {
    // Called by Login.js or AuthCallbackPage AFTER storing the token
    console.log("App.js: handleLoginSuccess called - Setting isAuthenticated = true");
    setIsAuthenticated(true);
    closeModal(); // Close modal if it was open
  }, [closeModal]); // Depends on closeModal

  const handleLogout = useCallback(() => {
    console.log("App.js: handleLogout called - Removing token and setting isAuthenticated = false");
    // Remove the token from localStorage
    localStorage.removeItem('authToken');
    // Set user as logged out
    setIsAuthenticated(false);
    // Note: Selected program state in context will reset if App re-renders or navigation changes scope.
  }, []); // Empty dependency array

  // --- Render Logic ---

  // Show loading indicator while checking initial authentication
  if (isLoadingAuth) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading Application...</h2>
        {/* Consider adding a more visually appealing spinner/loader here */}
      </div>
    );
  }

  // Main application structure
  return (
    // Wrap the Router with ProgramProvider to make context available
    <ProgramProvider>
      <Router basename="/admin">
        {/* AdminLayout provides the main structure (Sidebar + Content Area) */}
        <AdminLayout
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          isModalOpen={isModalOpen}
          modalContent={modalContent}
          openModal={openModal}
          closeModal={closeModal}
          onLoginSuccess={handleLoginSuccess} // Pass down for Login component
        >
          {/* Define application routes within AdminLayout's content area */}
          <Routes>
            {/* --- Authentication & Confirmation Routes --- */}
            <Route path="/confirm/:token" element={<ConfirmEmail />} />
            <Route
              path="/auth/callback"
              element={<AuthCallbackPage handleLoginSuccess={handleLoginSuccess} />}
            />

            {/* --- Core Pages --- */}
            <Route path="/dashboard" element={<DashboardPage isAuthenticated={isAuthenticated} />} />
            <Route path="/setup-program" element={<SetupProgramPage isAuthenticated={isAuthenticated} />} />
            <Route path="/select-program" element={<SelectProgramPage isAuthenticated={isAuthenticated} />} />
            <Route path="/manage-program" element={<ManageProgramPage isAuthenticated={isAuthenticated} />} />
            <Route path="/participants" element={<ParticipantsPage isAuthenticated={isAuthenticated} />} />
            <Route path="/scan-qr" element={<ScanQRPage isAuthenticated={isAuthenticated} />} />
            <Route path="/subscription" element={<SubscriptionPage isAuthenticated={isAuthenticated} />} />
            <Route path="/profile" element={<ProfilePage isAuthenticated={isAuthenticated} />} />

            <Route path="/rewards-settings" element={<RewardsPage isAuthenticated={isAuthenticated} />} />
            <Route path="/analytics" element={<AnalyticsPage isAuthenticated={isAuthenticated} />} />
            <Route path="/help" element={<HelpPage />} />

            {/* --- Default & Catch-all Routes --- */}
            {/* Redirect root path "/" to "/dashboard" */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            {/* Catch-all for any undefined paths */}
            <Route path="*" element={<div style={{padding: '20px'}}>Admin Page Not Found (404)</div>} />
          </Routes>
        </AdminLayout>
      </Router>
    </ProgramProvider>
  );
}

export default App;